import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Cloud, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock,
  Settings as SettingsIcon,
  Webhook,
  Database,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { getSyncHistory, getWebhookEvents, triggerSync, disconnectPMS } from '../../services/pmsApi';

const PMSDetailsModal = ({ isOpen, onClose, hotel, pmsStatus, onDisconnect, onSyncSuccess }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [syncHistory, setSyncHistory] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (isOpen && hotel) {
      fetchData();
    }
  }, [isOpen, hotel]);

  const fetchData = async () => {
    if (!hotel?.brand_id || !hotel?.id) return;

    setLoading(true);
    try {
      // Fetch sync history
      const historyData = await getSyncHistory(hotel.brand_id, hotel.id, 10);
      setSyncHistory(historyData.sync_logs || []);

      // Fetch webhook events
      const eventsData = await getWebhookEvents(hotel.brand_id, hotel.id, 20);
      setWebhookEvents(eventsData.webhook_events || []);
    } catch (error) {
      console.error('Error fetching PMS details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (syncType = 'all') => {
    if (!hotel?.brand_id || !hotel?.id) return;

    setSyncing(true);
    try {
      await triggerSync(hotel.brand_id, hotel.id, syncType);
      
      // Refresh data after sync
      setTimeout(() => {
        fetchData();
        if (onSyncSuccess) {
          onSyncSuccess();
        }
      }, 2000);
    } catch (error) {
      console.error('Error triggering sync:', error);
      alert(error.message || 'Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this PMS? This will stop all syncs and webhooks.')) {
      return;
    }

    if (!hotel?.brand_id || !hotel?.id) return;

    setDisconnecting(true);
    try {
      await disconnectPMS(hotel.brand_id, hotel.id);
      
      if (onDisconnect) {
        onDisconnect();
      }
      
      onClose();
    } catch (error) {
      console.error('Error disconnecting PMS:', error);
      alert(error.message || 'Failed to disconnect PMS');
    } finally {
      setDisconnecting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getSyncStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800">Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Cloud className="h-5 w-5" />
            <span>PMS Details - {hotel?.name}</span>
          </DialogTitle>
          <DialogDescription>
            View sync status, webhook events, and manage your PMS connection
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex space-x-1 border-b">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('sync')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'sync'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sync History
          </button>
          <button
            onClick={() => setActiveTab('webhooks')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'webhooks'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Webhooks
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {/* Connection Info */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h3 className="font-semibold text-sm mb-3 flex items-center space-x-2">
                      <Cloud className="h-4 w-4" />
                      <span>Connection Information</span>
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Provider:</span>
                        <p className="font-medium">{pmsStatus?.provider || 'Unknown'}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Property ID:</span>
                        <p className="font-medium">{pmsStatus?.propertyId || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <p className="font-medium text-green-600">Connected</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Last Sync:</span>
                        <p className="font-medium">{formatDate(pmsStatus?.lastSync)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-sm mb-3 flex items-center space-x-2">
                      <RefreshCw className="h-4 w-4" />
                      <span>Quick Actions</span>
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync('all')}
                        disabled={syncing}
                      >
                        {syncing ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Sync All
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync('rates')}
                        disabled={syncing}
                      >
                        <Database className="mr-2 h-4 w-4" />
                        Sync Rates
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync('reservations')}
                        disabled={syncing}
                      >
                        <Database className="mr-2 h-4 w-4" />
                        Sync Reservations
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync('room_types')}
                        disabled={syncing}
                      >
                        <Database className="mr-2 h-4 w-4" />
                        Sync Room Types
                      </Button>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-sm mb-3">Recent Activity</h3>
                    <div className="space-y-2">
                      {syncHistory.slice(0, 3).map((log, index) => (
                        <div key={index} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                          <div className="flex items-center space-x-2">
                            {log.sync_status === 'success' ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                            <span>{log.sync_type}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">{log.records_synced} records</span>
                            <span className="text-gray-400">{formatDate(log.started_at)}</span>
                          </div>
                        </div>
                      ))}
                      {syncHistory.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">No sync history yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Sync History Tab */}
              {activeTab === 'sync' && (
                <div className="space-y-2">
                  {syncHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No sync history yet</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => handleSync('all')}
                        disabled={syncing}
                      >
                        Trigger First Sync
                      </Button>
                    </div>
                  ) : (
                    syncHistory.map((log, index) => (
                      <div key={index} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm">{log.sync_type}</span>
                            {getSyncStatusBadge(log.sync_status)}
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(log.started_at)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>{log.records_synced} records synced</span>
                          {log.completed_at && (
                            <span>
                              Duration: {Math.round((new Date(log.completed_at) - new Date(log.started_at)) / 1000)}s
                            </span>
                          )}
                        </div>
                        {log.error_message && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                            <AlertCircle className="h-3 w-3 inline mr-1" />
                            {log.error_message}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Webhooks Tab */}
              {activeTab === 'webhooks' && (
                <div className="space-y-2">
                  {webhookEvents.length === 0 ? (
                    <div className="text-center py-12">
                      <Webhook className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No webhook events yet</p>
                      <p className="text-xs text-gray-400 mt-2">
                        Webhooks will appear here when events occur in your PMS
                      </p>
                    </div>
                  ) : (
                    webhookEvents.map((event, index) => (
                      <div key={index} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Webhook className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-sm">{event.event_type}</span>
                            {event.processed ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <Clock className="h-4 w-4 text-yellow-600" />
                            )}
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(event.received_at)}</span>
                        </div>
                        {event.payload && (
                          <div className="text-xs text-gray-600">
                            {event.payload.reservationID && (
                              <span>Reservation: {event.payload.reservationID}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button
            variant="destructive"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              'Disconnect PMS'
            )}
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PMSDetailsModal;
